import * as React from 'react';
import { Popover, PopoverSurface, Text, makeStyles, tokens } from '@fluentui/react-components';
import type { PositioningImperativeRef } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';

const useStyles = makeStyles({
  popoverContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    maxWidth: '270px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
  },
  heading: {
    fontSize: tokens.fontSizeBase600,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  stepIndicator: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  button: {
    minWidth: 0,
  },
});

export interface Step {
  title: string;
  content: string;
  targetRef: React.RefObject<HTMLElement>;
}

export interface TeachingPopoverProps {
  steps: Step[];
  open?: boolean;
  onComplete?: () => void;
  onDismiss?: () => void;
}

export const TeachingPopover = ({ steps, open = true, onComplete, onDismiss }: TeachingPopoverProps) => {
  const styles = useStyles();
  const positioningRef = React.useRef<PositioningImperativeRef>(null);
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [isOpen, setIsOpen] = React.useState(open);
  const { t } = useTranslation();
  const currentStep = steps[currentIndex];

  React.useEffect(() => {
    if (currentStep?.targetRef.current) {
      positioningRef.current?.setTarget(currentStep.targetRef.current);
    }
  }, [currentIndex, currentStep]);

  if (!isOpen || !currentStep) return null;

  const handleNext = () => {
    if (currentIndex === steps.length - 1) {
      setIsOpen(false);
      onComplete?.();
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleDismiss = () => {
    setIsOpen(false);
    onDismiss?.();
  };

  return (
    <>
      <Popover
        open={isOpen}
        positioning={{
          position: 'below',
          align: 'start',
          offset: { crossAxis: 0, mainAxis: 8 },
          positioningRef,
        }}
        withArrow
      >
        <PopoverSurface>
          <div className={styles.popoverContent}>
            <div className={styles.header}>
              <Text className={styles.heading}>{currentStep.title}</Text>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={handleDismiss}
              />
            </div>

            <Text>{currentStep.content}</Text>

            <div className={styles.footer}>
              <Text className={styles.stepIndicator}>{t('TEACHING_POPOVER.STEP_COUNT', { current: currentIndex + 1, total: steps.length })}</Text>
              <Button
                appearance="primary"
                onClick={handleNext}
                className={styles.button}
              >
                {t(currentIndex === steps.length - 1 ? 'TEACHING_POPOVER.FINISH' : 'TEACHING_POPOVER.NEXT')}
              </Button>
            </div>
          </div>
        </PopoverSurface>
      </Popover>
    </>
  );
};
